import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { pick } from 'lodash';
import {
  getFormValues,
  change,
  reset,
  isPristine,
  getFormSyncErrors,
  getFormInitialValues,
} from 'redux-form';

import { Filter, Main } from 'Containers/';
import { YesNoDialog } from 'Components/';
import { CREATE_EDIT_RECEIPT_INVENTORY_FORM_NAME } from 'Common/FormEnum';
import { CreateEditReceiptInventoryEnum as StatusEnum } from 'Common/Constants/Enumeration/ManagePurchaseOrderEnum';
import { TableNamesEnum } from 'Common/Constants/Enumeration/TableEnum';
import { toastError, toastSuccess, errorToast } from 'Common/ToastHelper';
import * as InventorySelectors from 'State/filters/selectors';
import * as InventoryActions from 'State/filters/actions';
import { PreferencesEnum } from 'Common/Security/PreferencesEnum';
import {
  DATE_FORMAT_FOR_SERVER,
  UNSAVED_MESSAGE,
  DATE_FORMAT_FOR_UI,
} from 'Common/Constants';
import {
  CreateEditReceiptInventoryEnum as FieldEnum,
  ItemEnum,
  ReceiptSaveOptionEnum,
} from 'Common/FieldEnum';

import { CreateEditReceiptForm } from '../../components/createEditReceipt/CreateEditReceiptForm';
import * as CreateEditReceiptSelectors from '../../state/createEditReceipt/selectors';
import * as CreateEditReceiptActions from '../../state/createEditReceipt/actions';
import { GridEnum } from 'Common/Constants/Enumeration/GridEnum';

class CreateEditReceiptContainer extends Component {
  static path =
    '/managepurchaseorder/purchase/receipt/:IsNewEdit/:ReceiptId/:ReceiptType';

  state = {
    showUnsavedModal: false,
    unsavedChanged: null,
    showReceiptDateModal: false,
    saveOption: null,
    showResetModal: false,
  };

  rootUrl = '/managepurchaseorder';

  componentDidMount() {
    const {
      match: {
        params: { ReceiptId },
      },
      getVendorForCurrentSite,
      getReceiptDetails,
    } = this.props;

    getVendorForCurrentSite();

    if (parseInt(ReceiptId, 10)) {
      getReceiptDetails(ReceiptId);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      getReceiptDetailsResult,
      getAllSiteItemsResult,
      postSaveReceiptResult,
      clearPostSaveReceiptResult,
      history,
      formValues: { [FieldEnum.POItemDetails]: orderItems },
      change,
    } = this.props;

    //if we were able to get existing receipt details, load items
    if (getReceiptDetailsResult !== prevProps.getReceiptDetailsResult) {
      const receiptId = getReceiptDetailsResult[FieldEnum.ReceiptId];

      this.updateSiteItems(
        getReceiptDetailsResult[FieldEnum.SiteId],
        getReceiptDetailsResult[FieldEnum.ReceiptDt],
        parseInt(receiptId, 10) ? receiptId : null
      );
    }

    //if our delivery date or site has changed, remove any items from our order
    //that are no longer in our site
    if (getAllSiteItemsResult !== prevProps.getAllSiteItemsResult) {
      const newOrderItems = { ...orderItems };
      let didRemove = false;

      for (let itemCode in newOrderItems) {
        if (newOrderItems.hasOwnProperty(itemCode)) {
          const siteItem = getAllSiteItemsResult.find(
            (item) => item[ItemEnum.ItemCode] === itemCode
          );

          if (!siteItem) {
            delete newOrderItems[itemCode];
            didRemove = true;
          }
        }
      }

      if (didRemove) {
        change(FieldEnum.POItemDetails, newOrderItems);
      }
    }

    //response from our save request, either show an error or redirect
    if (errorToast(this.props, prevProps, 'postSaveReceiptErrors')) {
      clearPostSaveReceiptResult();
    }

    if (postSaveReceiptResult && !prevProps.postSaveReceiptResult) {
      toastSuccess('Receipt has been saved.');
      history.push(this.rootUrl);
    }
  }

  componentWillUnmount() {
    this.props.clearAll();
  }

  handleSiteAndReceiptDateChange = (newValue, oldValue, field, confirmed) => {
    //apply incoming value to formValues
    const newValues = { ...this.props.formValues };

    if (field) {
      newValues[field] = newValue;
    }

    const {
      [FieldEnum.SiteId]: siteId,
      [FieldEnum.ReceiptDt]: receiptDate,
      [FieldEnum.POItemDetails]: orderItems,
    } = newValues;

    if (!siteId || !receiptDate) {
      return;
    }

    //show warning modal when trying to change site or date with items in order
    if (Object.keys(orderItems).length && !confirmed) {
      this.setState({
        showUnsavedModal: true,
        unsavedChange: {
          field,
          oldValue,
          newValue,
        },
      });

      return;
    }

    this.updateSiteItems(siteId, receiptDate);
  };

  updateSiteItems = (siteId, receiptDate, receiptId) => {
    const { getAllSiteItems, clearGetAllSiteItemsResult } = this.props;

    //current order items will be handled in componentDidUpdate
    if (parseInt(siteId, 10) && parseInt(receiptDate, 10)) {
      getAllSiteItems(
        siteId,
        moment(receiptDate).format(DATE_FORMAT_FOR_SERVER),
        receiptId
      );
    } else {
      clearGetAllSiteItemsResult();
    }
  };

  handleUnsavedModalResponse = (response) => {
    const { field, oldValue, newValue } = this.state.unsavedChange;

    if (response) {
      //if we've confirmed that we want to make changes anyway, make changes
      this.handleSiteAndReceiptDateChange(newValue, oldValue, field, true);
    } else {
      //otherwise change the form value back to what it was
      this.props.change(field, oldValue);
    }

    this.setState({ showUnsavedModal: false, unsavedChange: null });
  };

  handleSubmit = (saveOption, confirmed) => {
    const {
      formValues,
      formErrors,
      match: {
        params: { ReceiptId },
      },
      getReceiptDetailsResult,
      postSaveReceipt,
    } = this.props;

    //if there are any errors, show a toast
    const errorStrings = Object.values(formErrors);

    if (errorStrings.length) {
      for (let i = 0; i < errorStrings.length; i++) {
        toastError(errorStrings[i]);
      }

      return;
    }

    //if our receipt date is > 30 days in the future, show a confirmation
    const receiptDateMoment = moment(formValues[FieldEnum.ReceiptDt]);

    if (receiptDateMoment.isAfter(moment().add(30, 'days')) && !confirmed) {
      this.setState({
        showReceiptDateModal: true,
        saveOption,
      });

      return;
    }

    const receiptDate = receiptDateMoment.format(DATE_FORMAT_FOR_SERVER);

    //serialize items from object to array
    const orderItems = Object.values(formValues[FieldEnum.POItemDetails]).map(
      (item) => {
        const { _added, ...orderItem } = item;

        return {
          Id: 0,
          ...orderItem,
          [ItemEnum.PrimaryQty]: item[ItemEnum.PriQty],
          [ItemEnum.SecondaryQty]: item[ItemEnum.SecQty],
        };
      }
    );

    //build our payload
    const isWarehouseOrder = getReceiptDetailsResult
      ? getReceiptDetailsResult[FieldEnum.IsWarehouseOrder]
      : false;

    const orderDate = formValues[FieldEnum.OrderDT]
      ? moment(formValues[FieldEnum.OrderDT]).format(DATE_FORMAT_FOR_SERVER)
      : null;

    const data = {
      [FieldEnum.POWONum]: formValues[FieldEnum.POWONum],
      [FieldEnum.IsWarehouseOrder]: isWarehouseOrder,
      [FieldEnum.WarehouseSiteId]: isWarehouseOrder
        ? getReceiptDetailsResult[FieldEnum.WarehouseSiteId]
        : null,
      [FieldEnum.ReceiptId]: ReceiptId,
      [FieldEnum.ReceiptDt]: receiptDate,
      [FieldEnum.OrderId]: formValues[FieldEnum.OrderId],
      [FieldEnum.OrderDT]: orderDate,
      [FieldEnum.SiteId]: formValues[FieldEnum.SiteId],
      [FieldEnum.VendorId]: formValues[FieldEnum.VendorId],
      [FieldEnum.Invoice]: formValues[FieldEnum.Invoice],
      [FieldEnum.Comments]: formValues[FieldEnum.Comments],
      [FieldEnum.POItemDetails]: orderItems,
      [FieldEnum.SaveOption]: saveOption,
    };

    postSaveReceipt(data);
  };

  handleReceiptDateModalResponse = (response) => {
    if (response) {
      this.handleSubmit(this.state.saveOption, true);
    }

    this.setState({ showReceiptDateModal: false, saveOption: null });
  };

  handleReset = (confirmed) => {
    if (!confirmed) {
      this.setState({ showResetModal: true });
      return;
    }

    //compare current values to initial values
    //if site id or delivery date are different, fetch or clear site items
    const {
      [FieldEnum.SiteId]: siteId,
      [FieldEnum.ReceiptDt]: receiptDate,
    } = this.props.formValues;

    const {
      [FieldEnum.SiteId]: initialSiteId,
      [FieldEnum.ReceiptDt]: initialReceiptDate,
    } = this.getInitialValues();

    if (siteId !== initialSiteId || receiptDate !== initialReceiptDate) {
      this.updateSiteItems(initialSiteId, initialReceiptDate);
    }

    //reset the redux form to its initial values
    this.props.reset();
  };

  handleResetModalResponse = (response) => {
    if (response) {
      this.handleReset(true);
    }

    this.setState({ showResetModal: false });
  };

  getBreadcrumbs = () => {
    const { IsNewEdit } = this.props.match.params;

    const breadcrumbs = [
      {
        title: 'Manage Receipts',
        link: this.rootUrl,
      },
      {
        title: IsNewEdit === StatusEnum.EDIT ? 'Edit Receipt' : 'Create New',
        link: '',
      },
    ];

    return breadcrumbs;
  };

  getMainProps = () => {
    const {
      match: {
        params: { IsNewEdit },
      },
      formValues: { [FieldEnum.POItemDetails]: orderItems = {} },
      initialFormValues: { [FieldEnum.POItemDetails]: originalOrderItems },
      getReceiptDetailsResult,
      postSaveReceiptLoading,
      formPristine,
    } = this.props;

    const orderItemsEntries = Object.entries(orderItems);
    const hasOrderItems = Boolean(orderItemsEntries.length);

    const toolbox = {
      shown: true,
      backButtonProps: {
        shown: true,
        path: this.rootUrl,
        disabled: false,
      },
      rightButtons: [
        {
          content: 'Reset',
          onClick: () => this.handleReset(),
          disabled: formPristine || postSaveReceiptLoading,
        },
        {
          content: 'Update On Hand and Close',
          onClick: () =>
            this.handleSubmit(ReceiptSaveOptionEnum.UpdateAndClose),
          loading: postSaveReceiptLoading,
          disabled: !hasOrderItems || postSaveReceiptLoading || formPristine,
        },
        {
          content: 'Save Only',
          onClick: () => this.handleSubmit(ReceiptSaveOptionEnum.SaveOnly),
          loading: postSaveReceiptLoading,
          disabled: !hasOrderItems || postSaveReceiptLoading || formPristine,
        },
      ],
    };

    //users can only update on hand + keep open if the receipt is being edited,
    //the receipt has an order attached, and the receipt is being edited to have
    //a lower quantity on at least one item
    if (IsNewEdit === StatusEnum.EDIT) {
      const hasOrder = Boolean(
        getReceiptDetailsResult && getReceiptDetailsResult[FieldEnum.OrderId]
      );

      const canKeepOpen = orderItemsEntries.some(([itemCode, orderItem]) => {
        const originalOrderItem = originalOrderItems[itemCode];

        if (!originalOrderItem) {
          return false;
        }

        return (
          orderItem[ItemEnum.PriQty] < originalOrderItem[ItemEnum.PriQty] ||
          orderItem[ItemEnum.SecQty] < originalOrderItem[ItemEnum.SecQty]
        );
      });

      toolbox.rightButtons.splice(1, 0, {
        content: 'Update On Hand and Keep Open',
        onClick: () =>
          this.handleSubmit(ReceiptSaveOptionEnum.UpdateAndKeepOpen),
        loading: postSaveReceiptLoading,
        disabled:
          !hasOrderItems ||
          postSaveReceiptLoading ||
          formPristine ||
          !hasOrder ||
          !canKeepOpen,
      });
    }

    return { toolbox };
  };

  getInitialValues = () => {
    const {
      match: {
        params: { SiteId },
      },
      getReceiptDetailsResult,
    } = this.props;

    //if we're editing an order, use those values first
    let existingValues = {};

    if (getReceiptDetailsResult) {
      existingValues = pick(getReceiptDetailsResult, [
        FieldEnum.ReceiptId,
        FieldEnum.SiteId,
        FieldEnum.VendorId, // receipts use ID normally
        FieldEnum.VendorName, // or name if coming from a PO/WO/KO/BO
        FieldEnum.Comments,
        FieldEnum.Invoice,
        FieldEnum.POWONum,
        FieldEnum.OrderId,
      ]);

      //format dates
      existingValues[FieldEnum.ReceiptDt] = moment(
        getReceiptDetailsResult[FieldEnum.ReceiptDt]
      ).format(DATE_FORMAT_FOR_UI);

      const orderDT = getReceiptDetailsResult[FieldEnum.OrderDT];

      if (orderDT) {
        existingValues[FieldEnum.OrderDT] = moment(orderDT).format(
          DATE_FORMAT_FOR_UI
        );
      }

      //deserialize items from array to object
      existingValues[FieldEnum.POItemDetails] = getReceiptDetailsResult[
        FieldEnum.POItemDetails
      ].reduce(
        (items, item) => ({
          ...items,
          [item[ItemEnum.ItemCode]]: { ...item, _added: 0 },
        }),
        {}
      );
    }

    //defaults
    const initialValues = {
      [FieldEnum.ReceiptId]: '',
      [FieldEnum.ReceiptDt]: moment().format(DATE_FORMAT_FOR_UI),
      [FieldEnum.SiteId]: SiteId,
      [FieldEnum.VendorName]: '',
      [FieldEnum.VendorId]: 0,
      [FieldEnum.Comments]: '',
      [FieldEnum.POItemDetails]: {},
      ...existingValues,
    };

    return initialValues;
  };

  render() {
    const {
      vendorData,
      getAllSiteItemsLoading,
      getAllSiteItemsResult,
      getReceiptDetailsResult,
      match: {
        params: { IsNewEdit, ReceiptType },
      },
    } = this.props;

    const {
      showUnsavedModal,
      showReceiptDateModal,
      showResetModal,
    } = this.state;

    return (
      <React.Fragment>
        <Filter title="" breadcrumbs={this.getBreadcrumbs()} />

        <Main shouldNotUsePageWidth {...this.getMainProps()}>
          <CreateEditReceiptForm
            vendorData={vendorData}
            initialValues={this.getInitialValues()}
            receiptDetails={getReceiptDetailsResult}
            isNewEdit={IsNewEdit}
            receiptType={ReceiptType}
            onSiteAndReceiptDateChange={this.handleSiteAndReceiptDateChange}
            orderItemsTableName={
              TableNamesEnum.MANAGEPURCHASEORDER_CREATEEDIT_ORDERITEMS_TABLE
            }
            orderItemsGridName={
              GridEnum.INV_ManagePurchaseOrder_CreateEdit_OrderItems_View
            }
            siteItemsTableName={
              TableNamesEnum.MANAGEPURCHASEORDER_CREATEEDIT_SITEITEMS_TABLE
            }
            siteItemsGridName={
              GridEnum.INV_ManagePurchaseOrder_CreateEdit_SiteItems_View
            }
            siteItems={getAllSiteItemsResult}
            priceSecurityScope={PreferencesEnum.ReceiptPrice}
            disabled={false}
            loading={getAllSiteItemsLoading}
          />
        </Main>

        <YesNoDialog
          showDialog={showUnsavedModal}
          title="Unsaved changes detected"
          question={UNSAVED_MESSAGE}
          yesCallback={() => this.handleUnsavedModalResponse(true)}
          noCallback={() => this.handleUnsavedModalResponse(false)}
        />

        <YesNoDialog
          showDialog={showReceiptDateModal}
          title="Inventory"
          question="Delivery date is more than 30 days out, are you sure?"
          yesCallback={() => this.handleReceiptDateModalResponse(true)}
          noCallback={() => this.handleReceiptDateModalResponse(false)}
        />

        <YesNoDialog
          showDialog={showResetModal}
          title="Inventory"
          question="All of your unsaved changes will be lost, are you sure?"
          yesCallback={() => this.handleResetModalResponse(true)}
          noCallback={() => this.handleResetModalResponse(false)}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    formValues:
      getFormValues(CREATE_EDIT_RECEIPT_INVENTORY_FORM_NAME)(state) || {},
    initialFormValues:
      getFormInitialValues(CREATE_EDIT_RECEIPT_INVENTORY_FORM_NAME)(state) ||
      {},
    formPristine: isPristine(CREATE_EDIT_RECEIPT_INVENTORY_FORM_NAME)(state),
    formErrors: getFormSyncErrors(CREATE_EDIT_RECEIPT_INVENTORY_FORM_NAME)(
      state
    ),

    vendorData: InventorySelectors.getVendorDataWithEmpty(state),

    getAllSiteItemsLoading: CreateEditReceiptSelectors.getAllSiteItemsLoading(
      state
    ),
    getAllSiteItemsResult: CreateEditReceiptSelectors.getAllSiteItemsResult(
      state
    ),

    postSaveReceiptLoading: CreateEditReceiptSelectors.postSaveReceiptLoading(
      state
    ),
    postSaveReceiptResult: CreateEditReceiptSelectors.postSaveReceiptResult(
      state
    ),
    postSaveReceiptErrors: CreateEditReceiptSelectors.postSaveReceiptErrors(
      state
    ),

    getReceiptDetailsLoading: CreateEditReceiptSelectors.getReceiptDetailsLoading(
      state
    ),
    getReceiptDetailsResult: CreateEditReceiptSelectors.getReceiptDetailsResult(
      state
    ),
  };
};

const mapDispatchToProps = {
  change: (...args) => change(CREATE_EDIT_RECEIPT_INVENTORY_FORM_NAME, ...args),
  reset: () => reset(CREATE_EDIT_RECEIPT_INVENTORY_FORM_NAME),

  getVendorForCurrentSite: InventoryActions.getVendorForCurrentSite,
  getAllSiteItems: CreateEditReceiptActions.getAllSiteItems,
  clearGetAllSiteItemsResult:
    CreateEditReceiptActions.clearGetAllSiteItemsResult,
  postSaveReceipt: CreateEditReceiptActions.postSaveReceipt,
  clearPostSaveReceiptResult:
    CreateEditReceiptActions.clearPostSaveReceiptResult,
  getReceiptDetails: CreateEditReceiptActions.getReceiptDetails,

  clearAll: CreateEditReceiptActions.clearAll,
};

export const CreateEditReceipt = connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateEditReceiptContainer);



// WEBPACK FOOTER //
// ./src/pages/inventory/managepurchaseorder/containers/subpages/CreateEditReceipt.js